/**
 * extendMaterialShader — Safe `onBeforeCompile` orchestration for Three.js materials.
 *
 * Supports stacking multiple shader mods on a single material (e.g. AO + rim-light).
 * Each mod is identified by a string key tracked in `mat.userData.shaderMods`.
 * `customProgramCacheKey` is derived from the sorted set of active mods so that
 * Three.js re-uses compiled programs correctly across materials with the same mods.
 *
 * Usage:
 *   extendMaterialShader(mat, 'downAO:v1', (shader) => {
 *     shader.vertexShader = shader.vertexShader.replace(…);
 *     shader.fragmentShader = shader.fragmentShader.replace(…);
 *   });
 *
 * @see docs/ims-engine-api/06-feature-color-system.md
 */

import type { Material, WebGLProgramParametersWithUniforms } from 'three';

/**
 * Register a named shader mod on a Three.js material.
 *
 * - Wraps `onBeforeCompile` without clobbering existing callbacks.
 * - Tracks the mod key in `userData.shaderMods` (Set<string>).
 * - Derives `customProgramCacheKey` from the sorted mod set.
 * - Idempotent: calling with the same key twice is a no-op.
 *
 * @param mat     Three.js material (any subclass).
 * @param key     Unique stable key for this mod (e.g. 'downAO:v1').
 * @param patchFn Called during shader compilation to mutate vertex/fragment source.
 */
export function extendMaterialShader(
  mat: Material,
  key: string,
  patchFn: (shader: WebGLProgramParametersWithUniforms) => void,
): void {
  // Idempotency: skip if this mod is already registered.
  // Note: Three.js deep-clones userData via JSON round-trip, turning Set → {}.
  // Guard with instanceof so we replace stale non-Set values.
  const ud = mat.userData as Record<string, unknown>;
  if (!(ud.shaderMods instanceof Set)) {
    ud.shaderMods = new Set<string>();
  }
  const mods = ud.shaderMods as Set<string>;
  if (mods.has(key)) return;
  mods.add(key);

  // Chain: preserve any existing onBeforeCompile callback
  const prev = mat.onBeforeCompile?.bind(mat);

  mat.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms, renderer) => {
    prev?.(shader, renderer);
    patchFn(shader);
  };

  // Stable cache key derived from all active mods — ensures WebGLProgram reuse
  mat.customProgramCacheKey = () => Array.from(mods).sort().join('|');

  // Force recompilation on next render
  mat.needsUpdate = true;
}
